import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../pages/Contact.css';
import { 
  faPhone, 
  faEnvelope, 
  faMapMarkerAlt,
  faUser,
  faAt,
  faFileAlt,
  faComment
} from '@fortawesome/free-solid-svg-icons';
import { 
  faFacebookF, 
  faTwitter, 
  faInstagram, 
  faYoutube, 
  faLinkedinIn 
} from '@fortawesome/free-brands-svg-icons';
import logo from '../assets/logos/WHITE Logo Text PNG.png';
import { ref, push, set } from 'firebase/database';
import { managementDb } from '../firebase';
import { toast } from 'react-toastify';
import PageHero from '../components/PageHero';

function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',  
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMode, setFormMode] = useState('contact');
  const [panelOpen, setPanelOpen] = useState(true);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log('📝 Submitting contact form...');
      console.log('📊 Form data:', formData);
      
      // Create a new contact entry in Firebase
      const contactsRef = ref(managementDb, 'contacts');
      console.log('🔗 Contacts ref created:', contactsRef);
      
      const newContactRef = push(contactsRef);
      console.log('🆔 New contact ref created:', newContactRef);
      
      const contactData = {
        ...formData,
        timestamp: Date.now(),
        date: new Date().toISOString(),
        status: 'new', // new, read, responded
        intent: formMode
      };

      console.log('💾 Saving contact data:', contactData);
      await set(newContactRef, contactData);
      
      console.log('✅ Contact saved successfully');
      toast.success('Thank you for your message! We will get back to you soon.');
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('❌ Error submitting contact form:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerPanel = (mode) => {
    setFormMode(mode);
    setPanelOpen(true);
    requestAnimationFrame(() => {
      const target = document.getElementById('contact-form-panel');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  // If the URL contains ?form=quote, open the quote panel on mount
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const form = params.get('form');
    if (form === 'quote') {
      // Small timeout to ensure DOM is ready
      setTimeout(() => triggerPanel('quote'), 200);
    }
    // eslint-disable-next-line
  }, [location.search]);

  return (
    <>
      <PageHero
        title="Let's create something unforgettable"
        subtitle="Choose how you want to collaborate—quick hello or detailed project brief."
        align="left"
      />
      <div className="contact-container">
        <div className="contact-switcher">
          <button
            type="button"
            className={`switch-btn ${formMode === 'contact' ? 'active' : ''}`}
            onClick={() => triggerPanel('contact')}
          >
            Contact Us
          </button>
          <button
            type="button"
            className={`switch-btn ${formMode === 'quote' ? 'active' : ''}`}
            onClick={() => triggerPanel('quote')}
          >
            Get a Quote
          </button>
        </div>

      {/* Contact Cards - 3 in a row */}
      <div className="contact-cards-container">
        {/* Call Us Card */}
        <div className="contact-card">
          <div className="card-icon">
            <FontAwesomeIcon icon={faPhone} />
          </div>
          <h3>Call Us</h3>
          <p className="card-subtext">We're available Mon-Fri, 9am-6pm</p>
          <a href="tel:+917411611528" className="card-link">+91-74116 11528</a>
        </div>

        {/* Email Us Card */}
        <div className="contact-card">
          <div className="card-icon">
            <FontAwesomeIcon icon={faEnvelope} />
          </div>
          <h3>Email Us</h3>
          <p className="card-subtext">We'll respond within 24 hours</p>
          <a href="mailto:connect@thevideocompany.in" className="card-link">connect@thevideocompany.in</a>
        </div>

        {/* Visit Us Card */}
        <div className="contact-card">
          <div className="card-icon">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
          </div>
          <h3>Visit Us</h3>
          <p className="card-subtext">Our studio is open for meetings</p>
          <a 
            href="https://maps.app.goo.gl/8EWqWuqhjHFAVd2k7" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="card-link"
          >
            The Video Company, 318, Hosa Road Bengaluru, Karnataka 560068, India
          </a>
        </div>
      </div>

      {/* Combined Form and Logo Section */}
      <div className="contact-grid">
        <div
          className={`contact-panel ${panelOpen ? 'open' : ''}`}
          id="contact-form-panel"
        >
          <div className="panel-header">
            <h2>{formMode === 'contact' ? 'Send us a message' : 'Request a project quote'}</h2>
            <p>
              {formMode === 'contact'
                ? 'Tell us about your brief and we will respond within one business day.'
                : 'Share a few quick details and we will return with timelines, and crew plans.'}
            </p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name <span className="required-star">*</span></label>
                <div className="input-with-icon">
                  <FontAwesomeIcon icon={faUser} className="input-icon" />
                  <input 
                    type="text" 
                    id="firstName" 
                    name="firstName"
                    placeholder="Enter your first name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name <span className="required-star">*</span></label>
                <div className="input-with-icon">
                  <FontAwesomeIcon icon={faUser} className="input-icon" />
                  <input 
                    type="text" 
                    id="lastName" 
                    name="lastName"
                    placeholder="Enter your last name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address <span className="required-star">*</span></label>
              <div className="input-with-icon">
                <FontAwesomeIcon icon={faAt} className="input-icon" />
                <input 
                  type="email" 
                  id="email" 
                  name="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="phone">Mobile Number <span className="required-star">*</span></label>
              <div className="input-with-icon">
                <FontAwesomeIcon icon={faPhone} className="input-icon" />
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone"
                  placeholder="Enter your mobile number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="subject">Subject <span className="required-star">*</span></label>
              <div className="input-with-icon">
                <FontAwesomeIcon icon={faFileAlt} className="input-icon" />
                <input 
                  type="text" 
                  id="subject" 
                  name="subject"
                  placeholder="Enter message subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="message">Your Message</label>
              <div className="input-with-icon">
                <FontAwesomeIcon icon={faComment} className="input-icon textarea-icon" />
                <textarea 
                  id="message" 
                  name="message"
                  placeholder="Tell us about your project or inquiry..."
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginTop: '10px' }}>
              <button 
                type="submit" 
                className="submit-btn" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              <button
                type="button"
                className="clear-btn"
                disabled={isSubmitting}
                onClick={() => setFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  subject: '',
                  message: ''
                })}
              >
                Clear
              </button>
              <button
                type="button"
                className="clear-btn"
                style={{ background: '#1e5eab', color: 'white', border: 'none' }}
                onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLSefqB4bGeoKxFRVLo8i4NfHrkYQjr0Zn0Ru6vKyTfqwjEy8iQ/viewform?usp=header', '_blank')}
              >
                Get Quote
              </button>
            </div>
          </form>
        </div>

        {/* Logo Section */}
        <div className="logo-section">
          <div className="logo-container">
            <img src={logo} alt="The Video Company Logo" className="company-logo" />
          </div>
          <p className="company-tagline">
            Creating stunning visual stories that captivate and inspire audiences worldwide.
          </p>
          <div className="social-icons">
            <a href="https://facebook.com/" target="_blank" rel="noopener noreferrer" className="social-icon">
              <FontAwesomeIcon icon={faFacebookF} />
            </a>
            <a href="https://twitter.com/" target="_blank" rel="noopener noreferrer" className="social-icon">
              <FontAwesomeIcon icon={faTwitter} />
            </a>
            <a href="https://www.instagram.com/thevideocompany.in/" target="_blank" rel="noopener noreferrer" className="social-icon">
              <FontAwesomeIcon icon={faInstagram} />
            </a>
            <a href="https://www.youtube.com/@thevideocompanydotin" target="_blank" rel="noopener noreferrer" className="social-icon">
              <FontAwesomeIcon icon={faYoutube} />
            </a>
            <a href="https://www.linkedin.com/company/the-video-company-in/" target="_blank" rel="noopener noreferrer" className="social-icon">
              <FontAwesomeIcon icon={faLinkedinIn} />
            </a>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default Contact;